/**
 * TeamModeContext - единый контекст для режима команды
 *
 * Источник истины - Zustand store (persisted в localStorage).
 * Контекст ТОЛЬКО ЧИТАЕТ из store, НИКОГДА не мутирует его.
 * Переключение режима происходит ТОЛЬКО в ProfileScreen (с полной перезагрузкой).
 *
 * Компоненты должны использовать useTeamMode() вместо прямого доступа к store
 */

import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { useTeamStore, type Team } from '../stores/useTeamStore';

interface TeamModeContextValue {
  /** Находимся ли в режиме команды (из store) */
  isTeamMode: boolean;
  /** ID активной команды (null в личном режиме) */
  teamId: string | null;
  /** Полные данные команды из store */
  activeTeam: Team | null;
  /** Просматриваем ли чужой чат (read-only) */
  viewingOtherUserChat: boolean;
}

const TeamModeContext = createContext<TeamModeContextValue | null>(null);

interface TeamModeProviderProps {
  children: ReactNode;
  /** Режим из роута — используется только как fallback-hint, store имеет приоритет */
  mode: 'personal' | 'team';
}

/**
 * Провайдер контекста команды (read-only обёртка над store)
 * НЕ мутирует store — переключение только через ProfileScreen
 */
export function TeamModeProvider({ children }: TeamModeProviderProps) {
  // Читаем из store — единственный источник истины
  const { isTeamMode, activeTeam, viewingOtherUserChat } = useTeamStore();

  const value = useMemo<TeamModeContextValue>(() => ({
    isTeamMode,
    teamId: activeTeam?.id ?? null,
    activeTeam,
    viewingOtherUserChat
  }), [isTeamMode, activeTeam, viewingOtherUserChat]);

  return (
    <TeamModeContext.Provider value={value}>
      {children}
    </TeamModeContext.Provider>
  );
}

/**
 * Хук для доступа к контексту команды
 * @throws Error если используется вне TeamModeProvider
 */
export function useTeamMode(): TeamModeContextValue {
  const context = useContext(TeamModeContext);
  
  if (!context) {
    throw new Error('useTeamMode must be used within TeamModeProvider');
  }
  
  return context;
}

/**
 * Хук для безопасного доступа к контексту (не бросает ошибку)
 * Возвращает значения по умолчанию если контекст недоступен
 */
export function useTeamModeOptional(): TeamModeContextValue {
  const context = useContext(TeamModeContext);
  
  // Fallback для компонентов вне провайдера (account page, etc.)
  const { activeTeam, viewingOtherUserChat } = useTeamStore();
  
  if (!context) {
    return {
      isTeamMode: !!activeTeam,
      teamId: activeTeam?.id ?? null,
      activeTeam,
      viewingOtherUserChat
    };
  }
  
  return context;
}

export { TeamModeContext };

